import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  NgZone,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { TopProgressService } from './top-progress.service';

/** Work shorter than this never shows the bar, so fast navigations do not flash. */
export const TOP_PROGRESS_SHOW_DELAY_MS = 150;
/** Once shown, the bar stays at least this long. */
export const TOP_PROGRESS_MIN_VISIBLE_MS = 250;
/** One creep step; also the length of the transform transition between steps. */
export const TOP_PROGRESS_CREEP_INTERVAL_MS = 200;
/** How long the bar takes to fill on completion, and then to fade out. */
export const TOP_PROGRESS_FINISH_MS = 200;

const START = 0.08;
/** The creep approaches this and never reaches it; only completion fills the bar. */
const CREEP_LIMIT = 0.9;
const CREEP_FACTOR = 0.06;

type Phase = 'idle' | 'loading' | 'completing' | 'fading';

/**
 * The thin bar at the very top of the page while the app loads a screen or on-demand code.
 * Mounted once, at the app root. It reads `TopProgressService.active`.
 */
@Component({
  selector: 'stch-top-progress-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Overlaid above everything (CDK overlays sit at z-index 1000), never in the layout, never a click target.
  host: { class: 'pointer-events-none fixed inset-x-0 top-0 z-[10000] block h-[3px]' },
  template: `
    @if (phase() !== 'idle') {
      <div
        class="h-full w-full origin-left bg-primary"
        [class]="motionClass()"
        [style.transform]="transform()"
        [attr.role]="phase() === 'loading' ? 'progressbar' : null"
        [attr.aria-label]="phase() === 'loading' ? 'Loading' : null"
        [attr.aria-hidden]="phase() === 'loading' ? null : 'true'"
      ></div>
    }
  `,
})
export class TopProgressBarComponent {
  private readonly service = inject(TopProgressService);
  private readonly zone = inject(NgZone);
  private readonly window = inject(DOCUMENT).defaultView;

  protected readonly phase = signal<Phase>('idle');
  private readonly progress = signal(0);
  private readonly reducedMotion = signal(false);

  protected readonly transform = computed(() => `scaleX(${this.progress()})`);
  protected readonly motionClass = computed(() => {
    if (this.reducedMotion()) return '';
    if (this.phase() === 'fading') return 'opacity-0 transition-opacity duration-200 ease-out';
    return this.phase() === 'completing'
      ? 'transition-transform duration-200 ease-out'
      : 'transition-transform duration-200 ease-linear';
  });

  private shownAt = 0;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private finishTimer: ReturnType<typeof setTimeout> | null = null;
  private creepTimer: ReturnType<typeof setInterval> | null = null;

  public constructor() {
    effect(() => {
      const active = this.service.active();
      untracked(() => (active ? this.start() : this.stop()));
    });
    inject(DestroyRef).onDestroy(() => {
      this.clearShowTimer();
      this.clearFinishTimer();
      this.clearCreep();
    });
  }

  private start(): void {
    this.clearFinishTimer();
    const phase = this.phase();
    if (phase === 'idle') {
      // Timers run outside the zone: a creep tick must not run app-wide change detection, and a pending
      // timer must not keep the app from becoming stable. The signal writes still schedule rendering.
      this.showTimer ??= this.zone.runOutsideAngular(() => setTimeout(() => this.show(), TOP_PROGRESS_SHOW_DELAY_MS));
    } else if (phase !== 'loading') {
      // New work arrived while the bar was finishing: keep the same bar going.
      this.phase.set('loading');
      if (!this.reducedMotion()) {
        this.progress.set(Math.min(this.progress(), CREEP_LIMIT));
        this.startCreep();
      }
    }
  }

  private stop(): void {
    if (this.showTimer !== null) {
      this.clearShowTimer();
      return;
    }
    if (this.phase() !== 'loading') return;
    const remaining = Math.max(0, TOP_PROGRESS_MIN_VISIBLE_MS - (Date.now() - this.shownAt));
    this.finishTimer = this.zone.runOutsideAngular(() => setTimeout(() => this.finish(), remaining));
  }

  private show(): void {
    this.showTimer = null;
    this.shownAt = Date.now();
    const reduced = this.window?.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    this.reducedMotion.set(reduced);
    this.progress.set(reduced ? 1 : START);
    this.phase.set('loading');
    if (!reduced) this.startCreep();
  }

  private finish(): void {
    this.finishTimer = null;
    this.clearCreep();
    if (this.reducedMotion()) {
      this.reset();
      return;
    }
    this.phase.set('completing');
    this.progress.set(1);
    this.finishTimer = this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        this.phase.set('fading');
        this.finishTimer = this.zone.runOutsideAngular(() => setTimeout(() => this.reset(), TOP_PROGRESS_FINISH_MS));
      }, TOP_PROGRESS_FINISH_MS),
    );
  }

  private reset(): void {
    this.finishTimer = null;
    this.phase.set('idle');
    this.progress.set(0);
  }

  private startCreep(): void {
    this.clearCreep();
    this.creepTimer = this.zone.runOutsideAngular(() =>
      setInterval(
        () => this.progress.update((value) => value + (CREEP_LIMIT - value) * CREEP_FACTOR),
        TOP_PROGRESS_CREEP_INTERVAL_MS,
      ),
    );
  }

  private clearShowTimer(): void {
    if (this.showTimer !== null) clearTimeout(this.showTimer);
    this.showTimer = null;
  }

  private clearFinishTimer(): void {
    if (this.finishTimer !== null) clearTimeout(this.finishTimer);
    this.finishTimer = null;
  }

  private clearCreep(): void {
    if (this.creepTimer !== null) clearInterval(this.creepTimer);
    this.creepTimer = null;
  }
}
